import { useEffect, useLayoutEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { IconCheckCircle } from "../../../../../common/icons/IconCheckCircle/IconCheckCircle";
import LeafLinkButton from "../../../../../common/leaf/LeafLinkButton/LeafLinkButton";
import LeafToastMessage from "../../../../../common/leaf/LeafToastMessage/LeafToastMessage";
import { TAG_EXPORT_LIST, util as ExportAPIUtil } from "../../../../../services/ExportV2/exportV2API";
import { useExportNotificationData } from "./useExportNotificationData";
import defaultContent from "../../../../../content/serverSideExport";
import "./ServerSideExportCompletedNotify.scss";
import { STATUS_DONE, STATUS_ERROR } from "../../../../../common/constants/export-constants";
import { IconWarningCircle } from "../../../../../common/icons/IconWarningCircle/IconWarningCircle";
import { dispatchExportCountEvent } from "../../ServerSideExportUtil/ServerSideExportEvents";
import { useAppSelector } from "../../../../../store";
import { updateExport, addDismissedExportId } from "../../../../../reducers/export";
import { isExportDisabledBySessionSsoOrigin } from "../../../../../common/util/exportUtil";

export type SSNotifyContent = {
  inProgressMessageTitle: string;
  inProgressMessageSubTitle: string;
  successMessageTitle: string;
  successMessageSubTitle: string;
  failedMessageTitle: string;
  failedMessageSubTitle: string;
  downloadButtonText: string;
};

type Props = {
  moduleId: string;
  notifyDownloadLinkOnClick: () => void;
};

const ServerSideExportCompletedNotify = ({ moduleId, notifyDownloadLinkOnClick }: Props) => {
  const { t } = useTranslation(["serverSideExport"]);

  const dispatch = useDispatch();
  const exportModules = useAppSelector((state) => state.exports.modules);
  const dismissedExportIds = useAppSelector((state) => state.exports.dismissedExportIds);

  const defaultNotificationContent = defaultContent.notifications;

  const translatedContent: SSNotifyContent = t("notifications", {
    returnObjects: true,
    defaultValue: defaultNotificationContent,
  }) as SSNotifyContent;

  const content: SSNotifyContent = useMemo(
    () => ({
      ...defaultNotificationContent,
      ...translatedContent,
    }),
    [translatedContent, defaultNotificationContent],
  );

  const { los, cml } = isExportDisabledBySessionSsoOrigin();
  const isExportDisabled = los && cml;

  const { data, exportNotificationQuery } = useExportNotificationData(moduleId, isExportDisabled);

  useLayoutEffect(() => {
    if (!isExportDisabled) {
      dispatch(ExportAPIUtil.invalidateTags([{ type: TAG_EXPORT_LIST, id: moduleId }]));
      exportNotificationQuery({ moduleId }, false);
    }
  }, [isExportDisabled, dispatch, exportNotificationQuery, moduleId]);

  useEffect(() => {
    if (data && !isExportDisabled) {
      dispatch(updateExport({ moduleId, data }));
    }
  }, [data, dispatch, moduleId, isExportDisabled]);

  // Dismiss exports after render to avoid state updates during render
  useEffect(() => {
    const { Exports = [] } = exportModules[moduleId] || {};
    const newExports = Exports.filter((exp) => !dismissedExportIds.includes(exp.ExportID));

    newExports.forEach(({ ExportID }) => {
      const isMessageAlreadyDisplayed = document.getElementById(`ss-export-toast-${ExportID}`) !== null;
      if (!isMessageAlreadyDisplayed) {
        dispatch(addDismissedExportId(ExportID));
      }
    });
  }, [exportModules, moduleId, dismissedExportIds, dispatch]);

  return useMemo(() => {
    const { Exports = [], VisibleExportRequestCount = 0, ExportCount = 0 } = exportModules[moduleId] || {};

    // Filter out exports that have already been notified AND initial exports (existed on page load)
    const newExports = Exports.filter(
      (exp) => !dismissedExportIds.includes(exp.ExportID) && !(exp as any).IsInitialExport,
    );

    // update lit component export button state
    dispatchExportCountEvent(ExportCount, VisibleExportRequestCount);

    const getContent = (status: string) => {
      switch (status) {
        case STATUS_ERROR: {
          return {
            title: content.failedMessageTitle,
            subTitle: content.failedMessageSubTitle,
          };
        }
        case STATUS_DONE:
        default:
          return {
            title: content.successMessageTitle,
            subTitle: content.successMessageSubTitle,
          };
      }
    };

    const getNotifyTheme = (status: string) => {
      switch (status) {
        case STATUS_ERROR:
          return "error";
        case STATUS_DONE:
        default:
          return "success";
      }
    };

    return (
      <div className="ss-export-notify-container">
        {newExports.map(({ ExportStatus, Done, ExportID }) => {
          const { title, subTitle } = getContent(ExportStatus);
          const theme = getNotifyTheme(ExportStatus);

          const isError = ExportStatus === STATUS_ERROR;

          const isMessageAlreadyDisplayed = document.getElementById(`ss-export-toast-${ExportID}`) !== null;
          if (isMessageAlreadyDisplayed) return <span key={ExportID} />;

          return (
            <div id={`ss-export-toast-${ExportID}`} key={ExportID}>
              <LeafToastMessage
                toastId={ExportID}
                key={ExportID}
                htmlContent={
                  <div className={`ss-export-toast`}>
                    <div className={`ss-export-toast__icon`}>
                      {isError && <IconWarningCircle />}
                      {Done && <IconCheckCircle size={`sm`} color={`green`} />}
                    </div>
                    <div className={`ss-export-toast__content`}>
                      <div className={"ss-export-toast__text"}>
                        <div className={`ss-export-toast__title`}>{title}</div>
                        <div className={`ss-export-toast__subtitle`}>{subTitle}</div>
                      </div>
                      {Done && (
                        <div className={`ss-export-toast__download-link`} data-testid="toast-download-link">
                          <LeafLinkButton
                            as="link"
                            onClick={notifyDownloadLinkOnClick}
                            theme={"green"}
                            label={content.downloadButtonText}
                            fontWeight={"bold"}
                          >
                            {content.downloadButtonText}
                          </LeafLinkButton>
                        </div>
                      )}
                    </div>
                  </div>
                }
                showToast={true}
                position={"top-center"}
                autoClose={10000}
                hideProgressBar={false}
                closeOnClick={false}
                pauseOnHover={false}
                draggable={false}
                theme={theme}
                className="export-notify-complete"
              />
            </div>
          );
        })}
      </div>
    );
  }, [exportModules, dismissedExportIds, moduleId, content, notifyDownloadLinkOnClick]);
};

export default ServerSideExportCompletedNotify;
